/**
 * Zusammenfuehren zweier Fassungen eines Eintrags (T-04).
 *
 * Regel (ADR 0006): Je Feld gewinnt die neuere Aenderung. Haben beide
 * Geraete seit dem letzten Abgleich dasselbe Feld unterschiedlich
 * geaendert, ist das ein Konflikt: Die neuere gewinnt trotzdem, aber der
 * unterlegene Wert wird gemeldet - nichts geht stillschweigend verloren.
 *
 * Loeschen ist ein Grabstein mit eigener Uhr (`x`). Er gewinnt gegen alle
 * aelteren Feldaenderungen; eine spaetere Bearbeitung holt den Eintrag
 * zurueck (mit Hinweis).
 *
 * Felder sind die obersten Eigenschaften eines Eintrags. Verschachtelte
 * Werte (z. B. Bewertungen im Journal) zaehlen als ein Feld.
 */
import { neuere, type Uhr } from '@/domain/sync/uhr';

export type Daten = Record<string, unknown>;
export type Uhren = Record<string, Uhr>;

/** Ein Eintrag samt Feld-Uhren. `d = null`: geloescht oder hier unbekannt. */
export interface Fassung {
  d: Daten | null;
  f: Uhren;
  /** Uhr der Loeschung (Grabstein). */
  x?: Uhr;
}

/** Der Stand des Servers beim letzten Abgleich - daran erkennt man, wer seitdem etwas geaendert hat. */
export interface Basis {
  f: Uhren;
  x?: Uhr;
}

export type KonfliktArt = 'feld' | 'geloescht' | 'wiederhergestellt';

export interface Konflikt {
  art: KonfliktArt;
  /** Bei `feld`: der Feldname. */
  feld?: string;
  /** Was nicht uebernommen wurde - bei `geloescht` der ganze Eintrag. */
  verloren?: unknown;
  gewonnen?: unknown;
}

/** Tiefer Vergleich fuer gespeicherte Werte (JSON-artig plus Byte-Felder). */
export function gleich(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (a instanceof Uint8Array || b instanceof Uint8Array) {
    if (!(a instanceof Uint8Array && b instanceof Uint8Array) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a).filter((k) => (a as Daten)[k] !== undefined);
  const kb = Object.keys(b).filter((k) => (b as Daten)[k] !== undefined);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => gleich((a as Daten)[k], (b as Daten)[k]));
}

/** Verwaltungsfelder: zusammengefuehrt wie alle anderen, aber nie als Konflikt gemeldet. */
const OHNE_HINWEIS = new Set(['updatedAt']);

const hat = (d: Daten | null, k: string) => d !== null && d[k] !== undefined;

const juengste = (f: Uhren): Uhr | undefined => Object.values(f).reduce<Uhr | undefined>((m, u) => neuere(m, u), undefined);

/** Ist der Eintrag am Ende vorhanden? Ja, wenn eine Feldaenderung juenger ist als die Loeschung. */
export function lebendig(f: Uhren, x: Uhr | undefined): boolean {
  const j = juengste(f);
  return j !== undefined && (x === undefined || j > x);
}

export interface Ergebnis {
  fassung: Fassung;
  konflikte: Konflikt[];
}

export function zusammenfuehren(lokal: Fassung, basis: Basis, fern: Fassung): Ergebnis {
  const konflikte: Konflikt[] = [];
  const f: Uhren = {};
  const d: Daten = {};
  const felder = new Set([...Object.keys(lokal.f), ...Object.keys(fern.f), ...Object.keys(lokal.d ?? {}), ...Object.keys(fern.d ?? {})]);

  for (const k of felder) {
    const ul = lokal.f[k] ?? '';
    const uf = fern.f[k] ?? '';
    // Fehlt einer Seite der Eintrag (geloescht oder unbekannt), kommt der Wert von der anderen.
    const nimmFern = lokal.d === null || (fern.d !== null && (uf > ul || (uf === ul && !hat(lokal.d, k) && hat(fern.d, k))));
    const quelle = nimmFern ? fern.d : lokal.d;
    if (hat(quelle, k)) d[k] = quelle![k];
    const u = neuere(lokal.f[k], fern.f[k]);
    if (u) f[k] = u;

    const ub = basis.f[k] ?? '';
    if (lokal.d && fern.d && !OHNE_HINWEIS.has(k) && ul !== ub && uf !== ub && ul !== uf && !gleich(lokal.d[k], fern.d[k])) {
      konflikte.push({ art: 'feld', feld: k, verloren: nimmFern ? lokal.d[k] : fern.d[k], gewonnen: d[k] });
    }
  }

  const x = neuere(lokal.x, fern.x);
  const bleibt = lebendig(f, x);
  const lokalGeloescht = lokal.x !== undefined && lokal.x !== basis.x;
  const fernGeloescht = fern.x !== undefined && fern.x !== basis.x;
  const lokalBearbeitet = Object.entries(lokal.f).some(([k, u]) => u !== (basis.f[k] ?? ''));
  const fernBearbeitet = Object.entries(fern.f).some(([k, u]) => u !== (basis.f[k] ?? ''));

  if (!bleibt && fernGeloescht && lokalBearbeitet && lokal.d) {
    // Hier bearbeitet, dort (spaeter) geloescht: die Bearbeitung zum Wiederherstellen aufheben.
    konflikte.length = 0;
    konflikte.push({ art: 'geloescht', verloren: lokal.d });
  } else if (bleibt && ((lokalGeloescht && fernBearbeitet) || (fernGeloescht && lokalBearbeitet))) {
    konflikte.push({ art: 'wiederhergestellt' });
  }

  return { fassung: { d: bleibt ? d : null, f, ...(x ? { x } : {}) }, konflikte };
}

/** Haben zwei Fassungen dieselben Uhren? Dann ist nichts hochzuladen. */
export function gleicheUhren(a: Pick<Fassung, 'f' | 'x'>, b: Pick<Fassung, 'f' | 'x'>): boolean {
  const ka = Object.keys(a.f);
  return a.x === b.x && ka.length === Object.keys(b.f).length && ka.every((k) => a.f[k] === b.f[k]);
}

/**
 * Eine lokale Aenderung stempeln: geaenderte Felder bekommen die neue Uhr,
 * eine Loeschung (`neu` fehlt) setzt den Grabstein.
 */
export function stempeln(alt: Daten | undefined, neu: Daten | undefined, vorher: Pick<Fassung, 'f' | 'x'>, uhr: Uhr): Pick<Fassung, 'f' | 'x'> {
  if (!neu) return { f: vorher.f, x: uhr };
  const f = { ...vorher.f };
  for (const k of new Set([...Object.keys(alt ?? {}), ...Object.keys(neu)])) {
    if (!alt || !gleich(alt[k], neu[k])) f[k] = uhr;
  }
  return vorher.x ? { f, x: vorher.x } : { f };
}
